import { useEffect, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { ProjectDot } from '@/components/ProjectDot';
import { useProjectMap } from '@/data/hooks';
import { cn } from '@/lib/cn';
import { timerButtonVars } from '@/lib/projectColors';
import { PillButton } from './TimerControls';
import { isBreak } from '@/timer/engine';
import type { TimerApi } from '@/timer/useTimer';

/** What F opens (8i): the clock and nothing else. Chrome fades after four still seconds. */
export function FullscreenTimer({ api, onExit }: { api: TimerApi; onExit: () => void }) {
  const { timer, clock, status, cycle, actions } = api;
  const projects = useProjectMap();
  const project = timer.projectId ? projects.get(timer.projectId) : null;
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    let t = setTimeout(() => setIdle(true), 4000);
    const wake = () => { setIdle(false); clearTimeout(t); t = setTimeout(() => setIdle(true), 4000); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onExit(); };
    window.addEventListener('pointermove', wake);
    window.addEventListener('keydown', onKey);
    void document.documentElement.requestFullscreen?.().catch(() => undefined);
    return () => { clearTimeout(t); window.removeEventListener('pointermove', wake); window.removeEventListener('keydown', onKey); if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined); };
  }, [onExit]);

  const running = timer.status === 'running';
  const fade = cn('transition-opacity duration-500', idle && 'opacity-0');
  return (
    <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-10 select-none bg-canvas text-ink" role="dialog" aria-label="Fullscreen timer">
      <div className={cn('absolute top-[26px] left-[30px] flex items-center gap-[9px] opacity-50', fade)}>
        <ProjectDot project={project} />
        <span className="text-[12.5px] font-bold">{project?.name ?? 'Timato'}</span>
      </div>
      <div className={cn('absolute top-[26px] right-[30px] text-[12px] font-bold text-ink-3', fade)}>
        <span className="font-mono">Esc</span> or <span className="font-mono">F</span> to exit
      </div>

      <div role="timer" aria-live="off" className="font-mono font-medium tabular-nums leading-[.9] tracking-[-.07em] text-[clamp(72px,17vw,220px)]">{clock}</div>

      <div className="flex flex-col items-center gap-4 text-center px-6">
        <p className="text-[18px] font-extrabold tracking-[-.02em] m-0 max-w-[70vw] truncate">{isBreak(timer.mode) ? status : timer.goalText || status}</p>
        <p className="text-[12.5px] font-bold text-ink-2 m-0">{timer.mode === 'track' ? 'Tracking' : `${cycle.session} · ${cycle.next}`}</p>
        {/* The same pill as the Timer screen, in the project's colour: going fullscreen must not change what the button looks like. */}
        <PillButton onClick={() => void actions.toggle()} tone="timer" style={timerButtonVars(project)} className={cn('min-w-[170px]', fade)}>
          {running ? <><Pause size={16} strokeWidth={2.5} /> Pause</> : <><Play size={16} strokeWidth={2.5} /> {timer.status === 'idle' ? 'Start' : 'Resume'}</>}
        </PillButton>
      </div>

      <p className={cn('absolute bottom-6 text-[11.5px] font-bold text-ink-3 m-0', fade)}>
        Controls fade after 4s · <span className="font-mono">Space</span> pause · <span className="font-mono">S</span> {timer.mode === 'track' ? 'finish' : 'skip'}
      </p>
    </div>
  );
}
